import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { Flex } from "rebass/styled-components";
import Lottie from "lottie-react";

import { SettingsContainer } from "../../../../components/molecules/settingsContainer";
import { Error } from "../../../../components/atoms/form/error";
import { Success } from "../../../../components/atoms/form/success";
import { InputText } from "MVVM/Components/Inputs";

import {
  xsollaModalOpen,
  StripePaystationState,
  ManageOnMobileModalState,
} from "../../../State";
import { URLS } from "../../../Utilities/Maps";
import { Body } from "../../../Components/Text";
import { Button } from "../../../Components/Button";
import Loading from "../../../../animations/loading.json";

import { ExternalLink } from "./styledComponents";
import { getUserGroupDisplayMap, formatDate } from "./utils";
import { Subscription } from "./Subscription";
import { SubscriptionViewModel } from "../ViewModel";
import { TSubscriptionStatus } from "./types";
import { useUser } from "../../../Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { getDaysSinceExpiry } from "MVVM/Utilities/TrialCountdown";
import { usePromoCodeRedemption } from "MVVM/Hooks/usePromoCodeRedemption";
import { StorageAddOnPicker } from "MVVM/Components/StorageAddOnPicker";

const ENDPOINT = process.env.REACT_APP_STRIPE_ENDPOINT;

const lottieStyle = {
  height: 28,
};

export const Subscriptions = () => {
  return (
    <>
      <SubscriptionCard />
      <StorageAddOnsCard />
    </>
  );
};

const SubscriptionCard = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "account.upgrade",
  });
  const { data, loading, error } = SubscriptionViewModel();

  if (loading) {
    return (
      <SettingsContainer
        title={t("manageSubscriptions")}
        mb={5}
        subtitle={undefined}
      >
        <Flex justifyContent={"center"}>
          <Lottie animationData={Loading} style={lottieStyle} loop={true} />
        </Flex>
      </SettingsContainer>
    );
  }

  if (error) {
    return (
      <SettingsContainer
        title={t("manageSubscriptions")}
        mb={5}
        subtitle={undefined}
      >
        <Error>
          Something went wrong while retrieving your subscription status. Please
          contact us on Discord or email us at support@allstar.gg
        </Error>
      </SettingsContainer>
    );
  }

  const subscription = data.subscriptionStatus.subscription;

  return (
    <>
      <SettingsContainer
        title={t("manageSubscriptions")}
        mb={5}
        subtitle={undefined}
      >
        <Card {...subscription} />
        <Body mt={6} color="chalk">
          {t("manageSubscriptionsBodyOne")}{" "}
          <ExternalLink
            pl={1}
            href={
              subscription.provider === "XSOLLA"
                ? "https://help.xsolla.com/"
                : "https://support.stripe.com/"
            }
            target="_blank"
          >
            {subscription.provider}
          </ExternalLink>{" "}
          {t("manageSubscriptionsBodyTwo")}
        </Body>
      </SettingsContainer>
      <PromoCodeCard subscription={subscription} />
    </>
  );
};

const PromoCodeCard = ({
  subscription,
}: {
  subscription: TSubscriptionStatus;
}) => {
  const { code, setCode, status, message, applyCode, canRedeem } =
    usePromoCodeRedemption("Profile Settings");

  if (
    !subscription.hasSubscription ||
    subscription.provider !== "STRIPE" ||
    !canRedeem
  )
    return null;

  return (
    <SettingsContainer title="Promo Code" mb={5} subtitle={undefined}>
      <Subscription
        title="Have a promo code?"
        subtitle="Apply it to your subscription. The discount starts on your next billing cycle"
      >
        {status === "success" ? (
          <Success color="envy">{message}</Success>
        ) : (
          <>
            <Flex justifyContent="center" sx={{ gap: 3 }} mt={2}>
              <InputText
                placeholder="Enter code"
                value={code}
                maxWidth="240px"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCode(e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") applyCode();
                }}
              />
              <Button
                size="small"
                classification="secondary"
                disabled={!code.trim() || status === "loading"}
                onClick={applyCode}
              >
                {status === "loading" ? "Applying..." : "Apply"}
              </Button>
            </Flex>
            {status === "error" && <Error>{message}</Error>}
          </>
        )}
      </Subscription>
    </SettingsContainer>
  );
};

const Card = (props: TSubscriptionStatus) => {
  const {
    provider,
    isPaid,
    userGroup,
    isRenewing,
    isUpgraded,
    chargeAmount,
    timePeriod,
    isAdmin,
    isExpiring,
    hasSubscription,
    dateNextCharge,
    expires,
  } = props;
  const { allstarUser } = useUser();
  const USER_GROUP_DISPLAY_MAP = getUserGroupDisplayMap(
    useShouldShowSubscriptionOverhaul(),
  );

  const StripePortalSession = useCallback(async () => {
    if (provider === "XSOLLA") return null;
    const res = await fetch(`${ENDPOINT}/stripe/v1/request`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${allstarUser.token}`,
      },
    });
    const data = await res.json();
    return data.portal as string;
  }, [allstarUser.token]);

  const history = useHistory();
  const [stripePaystation, setStripePaystation] = useRecoilState(
    StripePaystationState,
  );
  const [xsollaPaystation, setXsollaPaystation] =
    useRecoilState(xsollaModalOpen);
  const { status: trialCardStatus } = useTrialCardStatus();
  const setManageOnMobile = useSetRecoilState(ManageOnMobileModalState);

  // /subscriptions?addCard=1 — deep-link target for the trial emails: opens the
  // add-card checkout directly for eligible no-card trial users, no-ops for
  // everyone else. Waits for the live Stripe check before deciding, and strips
  // the param either way so back/refresh doesn't reopen the modal.
  const location = useLocation();
  const addCardDeepLinkHandled = useRef(false);
  useEffect(() => {
    if (addCardDeepLinkHandled.current || !trialCardStatus) return;
    const params = new URLSearchParams(location.search);
    if (params.get("addCard") !== "1") return;
    addCardDeepLinkHandled.current = true;
    params.delete("addCard");
    history.replace({ ...location, search: params.toString() });

    const eligible =
      allstarUser?.user?.userGroup?.isTrialPeriod &&
      trialCardStatus.trialing &&
      !trialCardStatus.hasCard;
    if (!eligible) return;

    analytics.track("Trial Add Card Clicked", { location: "Email Deep Link" });
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialCardSetup: true,
    });
  }, [trialCardStatus, location, history, allstarUser, setStripePaystation]);

  // Mobile in-app purchases must be managed in the originating store. The kanye
  // gateway exposes these booleans (derived from the credits provider), which
  // the subscription status provider (Stripe vs Xsolla only) can't tell us.
  const isAppleIapUser =
    allstarUser?.user?.usingAppStoreInAppPurchases === true;
  const isPlayStoreUser =
    allstarUser?.user?.usingPlayStoreInAppPurchases === true;
  const isMobileIapUser = isAppleIapUser || isPlayStoreUser;

  // ponytail: keeps this file's local portal fetch rather than
  // MVVM/Hooks/useManageSubscription, so this path shows no CancelRetention step
  // and no fallback link when the popup is blocked. Upgrade path: swap paystation
  // for useManageSubscription and delete StripePortalSession.
  const paystation = async (type: "subscribe" | "session") => {
    if (isMobileIapUser) {
      const store = isAppleIapUser ? "apple" : "google";
      analytics.track("Manage Subscription Mobile Redirect", {
        location: "Profile Settings",
        store,
      });
      return setManageOnMobile({ isOpen: true, store });
    }

    switch (provider) {
      case "XSOLLA":
        // map Stripe types to xsolla:
        let stateType: "manage" | "token" = "manage";
        if (type === "subscribe") stateType = "token";
        return setXsollaPaystation({
          ...xsollaPaystation,
          isModalOpen: true,
          type: stateType,
        });
      case "STRIPE":
        if (type === "session") {
          setStripePaystation({
            ...stripePaystation,
            isModalOpen: true,
            type,
          });
          const url = await StripePortalSession();
          // open in new tab and focus
          window?.open(`${url}`, "_blank")?.focus();
          // close the loading modal
          return setStripePaystation({
            ...stripePaystation,
            isModalOpen: false,
          });
        }

        return setStripePaystation({
          ...stripePaystation,
          isModalOpen: true,
          type,
        });
    }
  };

  //INTERNAL
  if (isAdmin) {
    return (
      <Subscription
        title="You are an Internal tester"
        subtitle={"You have nothing to do here"}
      />
    );
  }

  //TRIAL EXPIRED, BONUS DAYS STILL CLAIMABLE (ALL-1838) — these users read as
  //FREE with no subscription, so this must precede the !hasSubscription branch
  if (trialCardStatus?.reactivationEligible) {
    const claimDeadline = trialCardStatus.reactivationDeadline
      ? formatDate(new Date(trialCardStatus.reactivationDeadline * 1000))
      : null;
    return (
      <Subscription
        title="Unlimited (Trial ended)"
        warning={
          claimDeadline
            ? `Your 7 bonus days are unclaimed. Offer ends ${claimDeadline}`
            : "Your 7 bonus days are unclaimed"
        }
        subtitle="Add a card to unlock another week of Unlimited. You won't be charged until it's up"
      >
        <Button
          mt={[2]}
          size="small"
          classification="secondary"
          flex={true}
          onClick={() => {
            analytics.track("Trial Reactivation - Add Card Clicked", {
              location: "Profile Settings",
              daysSinceExpiry: getDaysSinceExpiry(
                trialCardStatus.reactivationDeadline,
              ),
            });
            setStripePaystation({
              isModalOpen: true,
              type: "subscribe",
              pendingEmail: false,
              shouldRefreshMtx: false,
              trialReactivation: true,
            });
          }}
        >
          Add a card and claim 7 days
        </Button>
      </Subscription>
    );
  }

  if (!hasSubscription) {
    return (
      <Subscription
        title="Enjoying Allstar?"
        subtitle="Support us by subscribing"
      >
        <Button
          mt={[2]}
          size="small"
          classification="secondary"
          flex={true}
          onClick={() => {
            history.push(URLS.UPGRADE);
          }}
        >
          Upgrade
        </Button>
      </Subscription>
    );
  }

  const expiryDate = formatDate(new Date(expires!));

  //TRIALING WITHOUT A CARD (no-card reverse trial)
  if (
    allstarUser?.user?.userGroup?.isTrialPeriod &&
    trialCardStatus?.trialing &&
    !trialCardStatus.hasCard
  ) {
    return (
      <Subscription
        title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (Trial)`}
        warning={`Your free trial ends on ${expiryDate}`}
        subtitle={`Add a payment method to keep ${
          USER_GROUP_DISPLAY_MAP[userGroup]
        } after your trial${
          trialCardStatus.extended ? "" : " and get 7 bonus trial days"
        }`}
      >
        <Button
          mt={[2]}
          size="small"
          classification="secondary"
          flex={true}
          onClick={() => {
            analytics.track("Trial Add Card Clicked", {
              location: "Profile Settings",
            });
            setStripePaystation({
              isModalOpen: true,
              type: "subscribe",
              pendingEmail: false,
              shouldRefreshMtx: false,
              trialCardSetup: true,
            });
          }}
        >
          Add payment method
        </Button>
      </Subscription>
    );
  }

  //PAID PRO AND IS EXPIRING (subscription exists)
  if (isUpgraded && isExpiring && isPaid && hasSubscription) {
    return (
      <>
        <Subscription
          title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (${timePeriod})`}
          warning={`Your ${USER_GROUP_DISPLAY_MAP[userGroup]} subscription will end on ${expiryDate}`}
          subtitle={`Renew subscription today to maintain your ${USER_GROUP_DISPLAY_MAP[userGroup]} account and all its features`}
        >
          <Button
            mt={[2]}
            size="small"
            classification="secondary"
            flex={true}
            onClick={() => {
              analytics.track(`User opened ${provider} Paystation`, {
                userGroup: userGroup,
                expectedExpiration: true,
              });
              paystation("session");
            }}
          >
            Renew subscription to Allstar {USER_GROUP_DISPLAY_MAP[userGroup]}
          </Button>
        </Subscription>
      </>
    );
  }

  //PAID PRO AND IS RENEWING
  if (isUpgraded && isRenewing) {
    return (
      <>
        <Subscription
          title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (${timePeriod})`}
          subtitle={`Your next payment of $${chargeAmount} (${timePeriod}) occurs on ${expiryDate}`}
        >
          <Button
            mt={[2]}
            size="small"
            classification="secondary"
            flex={true}
            onClick={() => {
              analytics.track(`User opened ${provider} Paystation`, {
                userGroup: userGroup,
                expectedRenewal: true,
              });
              paystation("session");
            }}
          >
            Manage Subscription
          </Button>
        </Subscription>
      </>
    );
  }

  //PAID PRO AND IS EXPIRING (subscription doesnt exist)
  if (isUpgraded && isExpiring && isPaid && !hasSubscription) {
    return (
      <>
        <Subscription
          title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (${timePeriod})`}
          warning={`Your ${USER_GROUP_DISPLAY_MAP[userGroup]} subscription will end on ${expiryDate}`}
          subtitle={`Resubscribe today to maintain your ${USER_GROUP_DISPLAY_MAP[userGroup]} account and all its features`}
        >
          <Button
            mt={[2]}
            size="small"
            classification="secondary"
            flex={true}
            onClick={() => {
              analytics.track(`User opened ${provider} Paystation`, {
                userGroup: userGroup,
                expectedExpiration: true,
              });
              paystation("subscribe");
            }}
          >
            Resubscribe
          </Button>
        </Subscription>
      </>
    );
  }

  //GIFTED PRO AND IS RENEWING (PAID)
  if (isUpgraded && !isPaid && isRenewing) {
    return (
      <>
        <Subscription
          title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (${timePeriod})`}
          warning={`Your gifted ${USER_GROUP_DISPLAY_MAP[userGroup]} subscription will end on ${expiryDate}`}
          subtitle={`Your next payment of $${chargeAmount} (${timePeriod}) occurs on ${formatDate(
            new Date(dateNextCharge!),
          )}`}
        >
          <Button
            mt={[2]}
            size="small"
            classification="secondary"
            flex={true}
            onClick={() => {
              analytics.track("User opened XSOLLA Paystation", {
                gifted: true,
                userGroup: userGroup,
                expectedRenewal: true,
              });
              paystation("session");
            }}
          >
            Manage Subscription
          </Button>
        </Subscription>
      </>
    );
  }

  //GIFTED PRO AND IS EXPIRING
  if (isUpgraded && !isPaid && isExpiring) {
    return (
      <>
        <Subscription
          title={`${USER_GROUP_DISPLAY_MAP[userGroup]} (Gifted)`}
          warning={`Your gifted ${USER_GROUP_DISPLAY_MAP[userGroup]} subscription will end on ${expiryDate}`}
          subtitle={`Add a new payment method today to maintain your ${USER_GROUP_DISPLAY_MAP[userGroup]} account and all its features`}
        >
          <Button
            mt={[2]}
            size="small"
            classification="secondary"
            flex={true}
            onClick={() => {
              analytics.track(`User opened ${provider} Paystation`, {
                userGroup: userGroup,
                expectedExpiration: true,
                gifted: true,
              });
              paystation("subscribe");
            }}
          >
            Add payment method
          </Button>
        </Subscription>
      </>
    );
  }
  /**
   * FREE AND NEVER BEEN PRO:
   *
   * FREE AND HAS BEEN PRO:
   *
   * PRO AND WILL RENEW:
   *
   * PRO AND IS SET TO RENEW: (BUT WE KNOW PAYMENT METHOD IS INVALID SINCE LAST PAYMENT):
   *
   * PRO AND IS EXPIRING:
   *
   * GIFTED PRO AND IS EXPIRING:
   *
   */
  if (hasSubscription) {
    return (
      <Subscription
        title={USER_GROUP_DISPLAY_MAP[userGroup]}
        subtitle="Update your payment method, view invoices, or cancel your plan"
      >
        <Button
          mt={[2]}
          size="small"
          classification="secondary"
          flex={true}
          onClick={() => {
            analytics.track(`User opened ${provider} Paystation`, {
              userGroup: userGroup,
              manageBilling: true,
            });
            paystation("session");
          }}
        >
          Manage Billing
        </Button>
      </Subscription>
    );
  }

  return <></>;
};

const StorageAddOnsCard = () => {
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();

  if (!showSubscriptionOverhaul) return null;

  return (
    <StorageAddOnPicker analyticsSource="Settings">
      {({ onClick, isEligible }) => {
        if (!isEligible) return null;
        return (
          <SettingsContainer
            title="Storage Add-ons"
            mb={5}
            subtitle={undefined}
          >
            <Subscription
              title="Expand your clip storage"
              subtitle="Add more clip storage to your subscription"
            >
              <Button
                size="small"
                classification="secondary"
                flex={true}
                onClick={onClick}
              >
                Manage Storage
              </Button>
            </Subscription>
          </SettingsContainer>
        );
      }}
    </StorageAddOnPicker>
  );
};
